import { memo, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import type { PickedReshapeMark } from "components/SmartSurveyor/SmartSurveyorV3/CableTakeoffTabV3/routeReshape";
import {
  DELETE_COLOR,
  DOOMED_DEVICE_RADIUS_RATIO,
  DOOMED_DEVICE_STROKE_WIDTH,
  PREVIEW_DASH,
  PREVIEW_STROKE_WIDTH,
  DRAW_LANDING_HIT_PX,
  EDGE_HIT_PX,
  GLYPH_ARM_RATIO,
  HANDLE_FILL,
  LANDING_OFFER_DASH,
  LANDING_OFFER_OPACITY,
  JOIN_TARGET_DASH,
  JOIN_TARGET_OPACITY,
  JOIN_TARGET_STROKE_WIDTH,
  LANDING_RING_PX,
  LANDING_RING_STROKE_WIDTH,
  NODE_HANDLE_HOVER_STROKE_WIDTH,
  NODE_HANDLE_STROKE,
  NODE_HANDLE_STROKE_WIDTH,
  NODE_HIT_PX,
  NODE_HOVER_RADIUS_PX,
  NODE_RADIUS_PX,
  SIGN_LEADER_DASH,
  SIGN_LEADER_STROKE_WIDTH,
  SIGN_GAP_PX,
  SIGN_GLYPH_STROKE_WIDTH,
  SIGN_HIT_PX,
  SIGN_OFFSET_PX,
  SIGN_RADIUS_PX,
  SIGN_STROKE_WIDTH,
  SELECTED_EDGE_OPACITY,
  SELECTED_EDGE_STROKE_WIDTH,
  STRANDED_DASH,
  STRANDED_STROKE_WIDTH,
} from "./constants";
import type { ReshapeHover, RouteNodeHandlesProps } from "./types";

const DEFAULT_RGB: [number, number, number] = [255, 255, 0];

// The drawn marks never take the pointer; the transparent targets below them
// do, and they are the exact radii the click is hit-tested with.
const MARK_STYLE = { pointerEvents: "none" } as const;

const NODE_TARGET_STYLE = { pointerEvents: "all", cursor: "pointer" } as const;
const SIGN_TARGET_STYLE = { pointerEvents: "all", cursor: "pointer" } as const;
const EDGE_TARGET_STYLE = {
  pointerEvents: "stroke",
  cursor: "pointer",
} as const;

// Two graph points are the same point when they are drawn on the same spot;
// the edge marks carry coordinates, not the keys they came from.
const near = (
  a: { x: number; y: number },
  b: { x: number; y: number },
): boolean => Math.abs(a.x - b.x) < 0.01 && Math.abs(a.y - b.y) < 0.01;

const lineOf = (points: { x: number; y: number }[]): string =>
  points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");

const plusPath = (x: number, y: number, arm: number): string =>
  `M${x - arm},${y} L${x + arm},${y} M${x},${y - arm} L${x},${y + arm}`;

// A nib drawing a line. It has to be nothing like the ⊕ that adds a joint:
// both wore a plus, and "what does this + i click it does nothing" is what one
// glyph for two verbs buys.
const drawPath = (x: number, y: number, arm: number): string =>
  [
    `M${x - arm},${y + arm} L${x + arm * 0.35},${y - arm}`,
    `M${x + arm * 0.35},${y - arm} L${x + arm},${y - arm * 0.4}`,
    `M${x + arm},${y - arm * 0.4} L${x - arm * 0.3},${y + arm}`,
    `M${x - arm},${y + arm} L${x - arm * 0.3},${y + arm}`,
  ].join(" ");

const crossPath = (x: number, y: number, arm: number): string =>
  `M${x - arm},${y - arm} L${x + arm},${y + arm} M${x + arm},${y - arm} L${x - arm},${y + arm}`;

export const RouteNodeHandles = memo(function RouteNodeHandles({
  view,
  rgb = DEFAULT_RGB,
  size,
  zoomLevel,
}: RouteNodeHandlesProps) {
  const [hovered, setHovered] = useState<ReshapeHover | null>(null);
  const { t } = useTranslation();

  const color = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
  // A handle is a SCREEN-space object. The whole overlay sits inside a
  // `scale(zoomLevel)` transform, so every radius is divided by it and comes
  // out the same size in device pixels at any zoom — which is what lets zooming
  // in separate two corners drawn on top of each other.
  const px = (devicePixels: number) => devicePixels / zoomLevel;
  const nodeRadius = px(NODE_RADIUS_PX);
  const nodeHoverRadius = px(NODE_HOVER_RADIUS_PX);
  const doomedRadius = size * DOOMED_DEVICE_RADIUS_RATIO;

  /**
   * A POINTER GESTURE IS THREE EVENTS, and owning only the last of them is
   * owning none.
   *
   * The panel runs the whole cables view in `pick_zone` mode, so on every
   * MOUSEUP that did not travel far enough to be a drag it calls `onMapClick` —
   * and mouseup is dispatched BEFORE click. With a sign up, that fall-through
   * dismissed it, React unmounted the sign, and the click that followed had no
   * element left to land on. **Both signs were dead on screen while every test
   * passed**, because `fireEvent.click` dispatches only the third event.
   *
   * So a mark swallows `mousedown` and `mouseup` as well. `mousedown` matters
   * too: it is what arms the panel's drag, and a handle is not something you
   * pan the sheet by.
   */
  const swallow = (event: MouseEvent<SVGElement>) => event.stopPropagation();

  const pickWith =
    (pick: PickedReshapeMark) => (event: MouseEvent<SVGElement>) => {
      event.stopPropagation();
      view.onMark(pick);
    };

  /**
   * One handle per CLUSTER of nodes.
   *
   * A device spurring off a trunk leaves a device node, a tap and an elbow
   * within a hair of each other, so three squares landed inside one ring and
   * "click the node" had three answers. Nodes closer together than a handle is
   * wide are one thing he can point at. Zooming in separates them and they
   * become individually targetable again, the same screen-space rule
   * everything else here follows.
   *
   * `lead` is what a click MEANS — a device or the board beats a bend, because
   * that is what he was pointing at. `joint` is the bend inside the cluster, if
   * there is one, and it is what carries the square and what ✥ moves. Keeping
   * only the lead is what made the joints disappear: a bend near a device was
   * absorbed into the device and then filtered out of the squares, which is
   * "the interactive reshaping with joints we had was better i do not know
   * where its gone".
   */
  const clusters = (() => {
    const reach = px(NODE_HIT_PX * 2);
    type Node = (typeof view.nodes)[number];
    const groups: { lead: Node; joint: Node | null }[] = [];
    for (const node of view.nodes) {
      const near = groups.find(
        (group) =>
          Math.hypot(group.lead.x - node.x, group.lead.y - node.y) <= reach,
      );
      if (!near) {
        groups.push({ lead: node, joint: node.isDraggable ? node : null });
        continue;
      }
      if (near.lead.isDraggable && !node.isDraggable) near.lead = node;
      if (!near.joint && node.isDraggable) near.joint = node;
    }
    return groups;
  })();

  // Every joint of the run, drawn at rest and grabbable through its ✥ sign.
  const joints = clusters
    .map((group) => group.joint)
    .filter((node): node is (typeof view.nodes)[number] => Boolean(node));

  const signOn = view.sign;
  // Drawing or carrying takes the plan over: the cable itself stops being
  // clickable, because the only click that means anything now is where this
  // one goes.
  const isBusy = Boolean(view.drawFrom || view.carrying);
  const hover = view.carrying ? null : hovered;

  const nodeAt = (key: string | null) =>
    key ? (view.nodes.find((node) => node.key === key) ?? null) : null;

  const drawFrom = nodeAt(view.drawFrom);
  const carrying = nodeAt(view.carrying);

  // What the next click would connect to, and the cable it would make — along
  // x then along y, exactly what he gets.
  //
  // It follows the CURSOR from the moment he takes ✎, and snaps to a node when
  // he is over one. Waiting for him to find a node first is what made ✎ look
  // dead: he pressed it, one faint ring appeared around the node he already had
  // selected, and nothing else on the sheet moved — "i click it does nothing".
  const candidate =
    drawFrom && hovered?.kind === "corner"
      ? (clusters
          .map((group) => group.lead)
          .find((node) => node.key === hovered.key) ?? null)
      : null;

  // Every bend he has placed, then the leg to wherever he is pointing. Two
  // clicks still give the plain L; the bends are what he adds when that is not
  // the shape he wants.
  const preview = (() => {
    if (!drawFrom) return null;
    const to = candidate ?? view.cursor ?? drawFrom;
    const points = [drawFrom, ...view.drawPoints, to];
    const out = [points[0]];
    for (let i = 1; i < points.length; i += 1) {
      const from = out[out.length - 1];
      const next = points[i];
      const square =
        Math.abs(from.x - next.x) <= 0.5 || Math.abs(from.y - next.y) <= 0.5;
      if (!square) out.push({ x: next.x, y: from.y });
      out.push(next);
    }
    return out;
  })();

  // The joint in his hand, and the lengths that would follow it there. Nothing
  // is written until he clicks, so this dashed shape IS the whole answer to
  // "where does it go if I let it go here".
  // It is drawn from the CLICK, at the joint's own place until the cursor has
  // moved. Waiting for the first mouse move left a beat where he had picked
  // something up and the plan had not said so.
  /**
   * What a drop would JOIN it to, while he is still holding it.
   *
   * Connectivity is node identity, so a joint that merely lands beside a length
   * is joined to nothing — and there is no way to tell by looking, which is why
   * he had to ask. So the thing it would weld to says so before he lets go: a
   * node rings, a length lights along its whole span.
   */
  const joinTarget = (() => {
    if (!carrying || !hovered) return null;
    if (hovered.kind === "corner") {
      const node = clusters
        .map((group) => group.lead)
        .find((lead) => lead.key === hovered.key);
      return node && node.key !== carrying.key
        ? ({ kind: "node", node } as const)
        : null;
    }
    const edge = view.edges.find((candidate) => candidate.key === hovered.key);
    return edge ? ({ kind: "edge", edge } as const) : null;
  })();

  const carriedBand = (() => {
    if (!carrying) return null;
    const at = view.cursor ?? carrying;
    const legs = view.edges
      .filter((edge) => near(edge.a, carrying) || near(edge.b, carrying))
      .map((edge) => (near(edge.a, carrying) ? edge.b : edge.a));
    return { at, legs };
  })();

  // The length he clicked: red, and offering its signs. Clicking it did NOT
  // cut it — "i click on edge it become red opens small remove sign there".
  const signedEdge =
    signOn?.kind === "edge"
      ? (view.edges.find((edge) => edge.key === signOn.key) ?? null)
      : null;
  const signedNode = signOn?.kind === "node" ? nodeAt(signOn.key) : null;

  /**
   * The signs on offer for whatever he clicked. A device or the board offers ✎
   * "draw a cable from here". A length offers ⊕ "put a joint here" and ✕
   * "remove it". **A joint offers nothing** — it is a handle, and a handle is
   * dragged, not commanded.
   *
   * They stack outward from the thing they act on so they never cover it, and
   * each one carries a hairline back to it so there is no doubt what it acts
   * on.
   */
  const signs = (() => {
    const made: {
      id: string;
      x: number;
      y: number;
      fromX: number;
      fromY: number;
      glyph: string;
      color: string;
      label: string;
      pick: PickedReshapeMark;
    }[] = [];
    const arm = px(SIGN_RADIUS_PX) * GLYPH_ARM_RATIO;
    const place = (fromX: number, fromY: number, index: number) => ({
      fromX,
      fromY,
      x: fromX + px(SIGN_OFFSET_PX) + index * px(SIGN_GAP_PX),
      y: fromY - px(SIGN_OFFSET_PX),
    });

    if (signedNode) {
      const draw = place(signedNode.x, signedNode.y, 0);
      made.push({
        id: "draw",
        label: t("smartSurveyor.cablesBeta.reshapeSignDraw"),
        ...draw,
        glyph: drawPath(draw.x, draw.y, arm),
        color,
        pick: { kind: "signDraw", nodeKey: signedNode.key },
      });
    }

    if (signedEdge) {
      // Too short for a joint of its own: the middle of it is already inside
      // both ends, so the new node could never be separated from them.
      if (signedEdge.hasAdd) {
        const joint = place(signedEdge.x, signedEdge.y, 0);
        made.push({
          id: "addJoint",
          label: t("smartSurveyor.cablesBeta.reshapeSignAddJoint"),
          ...joint,
          glyph: plusPath(joint.x, joint.y, arm),
          color,
          pick: { kind: "signAddJoint", edgeKey: signedEdge.key },
        });
      }
      const remove = place(signedEdge.x, signedEdge.y, made.length);
      made.push({
        id: "remove",
        label: t("smartSurveyor.cablesBeta.reshapeSignRemove"),
        ...remove,
        glyph: crossPath(remove.x, remove.y, arm),
        color: DELETE_COLOR,
        pick: { kind: "signRemove", edgeKey: signedEdge.key },
      });
    }
    return made;
  })();

  // Leaving a mark only clears the hover if nothing else has claimed it since.
  // Moving along a trunk fires out-then-over for the two lengths, and without
  // this the marks blink off and on at every boundary.
  const leave = (key: string) =>
    setHovered((held) => (held && held.key === key ? null : held));

  if (!view.edges.length && !view.nodes.length) return null;

  return (
    <g data-testid="route-reshape-layer">
      <g style={MARK_STYLE}>
        {/* "i click on edge it become red opens small remove sign there." The
            red IS the warning: it says this is the one that goes. It appears on
            the click and nothing has been cut. */}
        {view.edges
          .filter((edge) => edge.isSelected)
          .map((edge) => (
            <path
              key={`picked:${edge.key}`}
              data-testid="route-selected-edge"
              d={lineOf([edge.a, edge.b])}
              fill="none"
              stroke={DELETE_COLOR}
              strokeWidth={SELECTED_EDGE_STROKE_WIDTH}
              strokeLinecap="round"
              opacity={SELECTED_EDGE_OPACITY}
              vectorEffect="non-scaling-stroke"
            />
          ))}

        {/* A length no cable runs through. It is on the drawing and in no
            total, so it cannot look like the cable beside it. */}
        {view.edges
          .filter((edge) => !edge.isUsed)
          .map((edge) => (
            <path
              key={`stranded:${edge.key}`}
              data-testid="route-stranded-edge"
              d={lineOf([edge.a, edge.b])}
              fill="none"
              stroke={color}
              strokeWidth={STRANDED_STROKE_WIDTH}
              strokeDasharray={STRANDED_DASH}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}

        {/* A device the edit has cut off. It keeps its place on the survey and
            loses its cable, and it stays marked until he reconnects it or
            answers the question on the way out. */}
        {view.unreachable.map((device) => (
          <g key={`cut:${device.x}:${device.y}`}>
            <circle
              data-testid="route-cut-device"
              cx={device.x}
              cy={device.y}
              r={doomedRadius}
              fill="none"
              stroke={DELETE_COLOR}
              strokeWidth={DOOMED_DEVICE_STROKE_WIDTH}
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={crossPath(device.x, device.y, doomedRadius * GLYPH_ARM_RATIO)}
              fill="none"
              stroke={DELETE_COLOR}
              strokeWidth={DOOMED_DEVICE_STROKE_WIDTH}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ))}

        {/* Exactly the cable the second click will make — along x, then along
            y — and nothing at all until he is over a node he could connect to.
            A dashed line wandering to the pointer is most of what he could not
            read. */}
        {preview && (
          <path
            data-testid="route-connect-preview"
            d={lineOf(preview)}
            fill="none"
            stroke={color}
            strokeWidth={PREVIEW_STROKE_WIDTH}
            strokeDasharray={PREVIEW_DASH}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        )}

        {/* "…its not recognizing that they should become one route." It will
            now, and this is the plan saying so with the joint still in hand. */}
        {joinTarget?.kind === "edge" && (
          <path
            data-testid="route-join-target"
            d={lineOf([joinTarget.edge.a, joinTarget.edge.b])}
            fill="none"
            // INK, not the run's colour: this sits on top of a cable already
            // drawn in that colour over a white casing, on a white sheet, so
            // the run's own hue says nothing. The joint outline is ink for the
            // same reason and the two read as one vocabulary.
            stroke={NODE_HANDLE_STROKE}
            strokeWidth={JOIN_TARGET_STROKE_WIDTH}
            strokeDasharray={JOIN_TARGET_DASH}
            strokeLinecap="round"
            opacity={JOIN_TARGET_OPACITY}
            vectorEffect="non-scaling-stroke"
          />
        )}
        {joinTarget?.kind === "node" && (
          <circle
            data-testid="route-join-target"
            cx={joinTarget.node.x}
            cy={joinTarget.node.y}
            r={px(LANDING_RING_PX)}
            fill="none"
            stroke={NODE_HANDLE_STROKE}
            strokeWidth={JOIN_TARGET_STROKE_WIDTH}
            opacity={JOIN_TARGET_OPACITY}
            vectorEffect="non-scaling-stroke"
          />
        )}

        {carriedBand && (
          <g data-testid="route-carry-preview">
            {carriedBand.legs.map((leg, index) => (
              <path
                key={`carry:${index}`}
                data-testid="route-carry-leg"
                d={lineOf([leg, carriedBand.at])}
                fill="none"
                stroke={color}
                strokeWidth={PREVIEW_STROKE_WIDTH}
                strokeDasharray={PREVIEW_DASH}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <rect
              data-testid="route-carry-ghost"
              x={carriedBand.at.x - nodeHoverRadius}
              y={carriedBand.at.y - nodeHoverRadius}
              width={nodeHoverRadius * 2}
              height={nodeHoverRadius * 2}
              fill={HANDLE_FILL}
              stroke={NODE_HANDLE_STROKE}
              strokeWidth={NODE_HANDLE_HOVER_STROKE_WIDTH}
              vectorEffect="non-scaling-stroke"
            />
          </g>
        )}

        {/* Three pulses and rest: the marks arriving is the whole announcement
            that this run is now editable. Nothing here loops. */}
        <g className="karl-blink">
          {/* ◯ — a corner, drag it. The one he is carrying stays drawn, grown,
              because it is the shape he is moving. */}
          {/* A corner is a SQUARE. Everything else on this cable is round —
              the devices the run ends at, the ⊕ and the ⊗ — so the one mark
              that is a bend in the cable rather than a thing on it is the one
              mark with corners of its own. */}
          {joints.map((node) => {
            const isHovered =
              hover?.kind === "corner" && hover.key === node.key;
            const isHeld = view.carrying === node.key;
            // Held: the square stays where it came from, at rest size. The
            // grown one is under the cursor, and two big squares would be two
            // claims about where the joint is.
            const half = isHovered ? nodeHoverRadius : nodeRadius;
            return (
              <rect
                key={node.key}
                data-testid="route-node-handle"
                data-hovered={isHovered ? "true" : undefined}
                data-held={isHeld ? "true" : undefined}
                x={node.x - half}
                y={node.y - half}
                width={half * 2}
                height={half * 2}
                fill={HANDLE_FILL}
                stroke={NODE_HANDLE_STROKE}
                strokeWidth={
                  isHovered
                    ? NODE_HANDLE_HOVER_STROKE_WIDTH
                    : NODE_HANDLE_STROKE_WIDTH
                }
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {/* Where the click would land. On a corner it rings the square; on a
              device it rings the disc, which is the mark that is already
              there. Either way it is the one thing on the drawing saying "this
              is what you are about to join". */}
          {/* The turns he has already put in, so the stroke reads as a shape
              he is building rather than a line that keeps changing its mind. */}
          {drawFrom &&
            view.drawPoints.map((point, index) => (
              <rect
                key={`bend:${index}`}
                data-testid="route-draw-bend"
                x={point.x - nodeRadius}
                y={point.y - nodeRadius}
                width={nodeRadius * 2}
                height={nodeRadius * 2}
                fill={HANDLE_FILL}
                stroke={NODE_HANDLE_STROKE}
                strokeWidth={NODE_HANDLE_STROKE_WIDTH}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          {drawFrom && (
            <circle
              data-testid="route-armed-node"
              cx={drawFrom.x}
              cy={drawFrom.y}
              r={px(LANDING_RING_PX)}
              fill="none"
              stroke={color}
              strokeWidth={LANDING_RING_STROKE_WIDTH}
              vectorEffect="non-scaling-stroke"
            />
          )}
          {/* "i just select where should go this edge and thats it" — so every
              node he could select is ringed the moment he starts drawing, and
              the one under the cursor is ringed solid. Without these the plan
              said nothing about where the cable was allowed to end, and taking
              ✎ looked like pressing a dead button. */}
          {drawFrom &&
            clusters
              .map((group) => group.lead)
              .filter((node) => node.key !== drawFrom.key)
              .map((node) => (
                <circle
                  key={`offer:${node.key}`}
                  data-testid="route-landing-offer"
                  data-candidate={
                    candidate?.key === node.key ? "true" : undefined
                  }
                  cx={node.x}
                  cy={node.y}
                  r={px(LANDING_RING_PX)}
                  fill="none"
                  stroke={color}
                  strokeWidth={LANDING_RING_STROKE_WIDTH}
                  strokeDasharray={
                    candidate?.key === node.key ? undefined : LANDING_OFFER_DASH
                  }
                  opacity={
                    candidate?.key === node.key ? 1 : LANDING_OFFER_OPACITY
                  }
                  vectorEffect="non-scaling-stroke"
                />
              ))}
        </g>
      </g>

      {/* A sign is drawn as a CONTROL: a filled disc with a glyph, offset clear
          of the thing it acts on. Not another abstract mark — it is a button,
          and it is the only thing on the drawing that does anything. */}
      <g style={MARK_STYLE}>
        {signs.map((sign) => (
          <g key={`sign:${sign.id}`}>
            <line
              data-testid="route-sign-leader"
              x1={sign.fromX}
              y1={sign.fromY}
              x2={sign.x}
              y2={sign.y}
              stroke={sign.color}
              strokeWidth={SIGN_LEADER_STROKE_WIDTH}
              strokeDasharray={SIGN_LEADER_DASH}
              vectorEffect="non-scaling-stroke"
            />
            <circle
              data-testid="route-sign"
              data-sign={sign.id}
              cx={sign.x}
              cy={sign.y}
              r={px(SIGN_RADIUS_PX)}
              fill={HANDLE_FILL}
              stroke={sign.color}
              strokeWidth={SIGN_STROKE_WIDTH}
              vectorEffect="non-scaling-stroke"
            >
              <title>{sign.label}</title>
            </circle>
            <path
              data-testid="route-sign-glyph"
              data-glyph={sign.id === "addJoint" ? "plus" : sign.id}
              d={sign.glyph}
              fill="none"
              stroke={sign.color}
              strokeWidth={SIGN_GLYPH_STROKE_WIDTH}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ))}
      </g>

      {/* The targets. Paint order IS the precedence — the pointer resolves the
          topmost — and the SIGNS are on top of everything, because while one is
          up it is the only thing that may act. */}
      <g data-testid="route-reshape-targets">
        {/* The cable. Clicking it reddens the length and offers its signs; it
            has never, under this design, cut anything. */}
        {view.edges.map((edge) => (
          <path
            key={`body:${edge.key}`}
            data-testid="route-edge-target"
            d={lineOf([edge.a, edge.b])}
            fill="none"
            stroke="transparent"
            strokeWidth={px(EDGE_HIT_PX) * 2}
            style={EDGE_TARGET_STYLE}
            onMouseDown={swallow}
            onMouseUp={swallow}
            onClick={pickWith({ kind: "edge", edgeKey: edge.key })}
            onMouseOver={() => setHovered({ kind: "edge", key: edge.key })}
            onMouseOut={() => leave(edge.key)}
          />
        ))}

        {/* The nodes, and what a click on one MEANS. The clustering lives here,
            so the answer is decided here rather than guessed at by the edit
            layer, which cannot see which nodes fell into one handle:

              • carrying a joint    -> JOIN it to this one, which is what makes
                                       two pieces one route
              • mid-draw            -> land the cable on it
              • a cluster with a JOINT in it -> pick that joint up. A handle is
                dragged; it is the interactivity he asked for twice.
              • otherwise (a device, the board) -> reveal ✎

            Carrying or drawing, the target grows to cover the whole device
            disc, because by then there is nothing else he could mean. */}
        {clusters
          .filter((group) =>
            carrying
              ? group.lead.key !== carrying.key
              : drawFrom
                ? group.lead.key !== drawFrom.key
                : true,
          )
          .map(({ lead, joint }) => (
            <circle
              key={lead.key}
              data-testid="route-node-target"
              data-means={
                carrying ? "join" : drawFrom ? "land" : joint ? "grab" : "sign"
              }
              cx={lead.x}
              cy={lead.y}
              r={px(isBusy ? DRAW_LANDING_HIT_PX : NODE_HIT_PX)}
              fill="transparent"
              style={NODE_TARGET_STYLE}
              onMouseDown={swallow}
              onMouseUp={swallow}
              onClick={pickWith(
                !isBusy && joint
                  ? { kind: "grabJoint", nodeKey: joint.key }
                  : { kind: "node", nodeKey: lead.key },
              )}
              onMouseOver={() => setHovered({ kind: "corner", key: lead.key })}
              onMouseOut={() => leave(lead.key)}
            />
          ))}

        {signs.map((sign) => (
          <circle
            key={`sign-target:${sign.id}`}
            data-testid="route-sign-target"
            data-sign={sign.id}
            cx={sign.x}
            cy={sign.y}
            r={px(SIGN_HIT_PX)}
            fill="transparent"
            style={SIGN_TARGET_STYLE}
            aria-label={sign.label}
            onMouseDown={swallow}
            onMouseUp={swallow}
            onClick={pickWith(sign.pick)}
          >
            <title>{sign.label}</title>
          </circle>
        ))}
      </g>
    </g>
  );
});
